import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import {
  border,
  color,
  compose,
  flexbox,
  layout,
  position,
  space,
  system,
} from 'styled-system';

import { forwardValidProp } from '../../utils/style';

const baseBoxStyles = ({ theme }) => css`
  background-color: ${theme.colors.box.bg};
  border-radius: ${theme.border.radius.size2};
  display: flex;
  flex-direction: column;
`;

const fullBoxStyles = ({ full }) =>
  full &&
  css`
    width: 100%;
  `;

const StyledBox = styled('div', {
  shouldForwardProp: (prop) => forwardValidProp(prop),
})`
  ${baseBoxStyles}
  ${fullBoxStyles}
  ${system({
    gridColumnEnd: { property: 'gridColumnEnd' },
    gridColumnStart: { property: 'gridColumnStart' },
    gridRowEnd: { property: 'gridRowEnd' },
    gridRowStart: { property: 'gridRowStart' },
  })}
  ${compose(border, color, flexbox, layout, space, position)}
`;

const Box = forwardRef(({ ...props }, ref) => (
  <StyledBox ref={ref} {...props} />
));

Box.displayName = 'Box';

Box.defaultProps = {
  children: null,
  full: false,
};

Box.propTypes = {
  /**
   * Components to be rendered inside component
   */
  children: PropTypes.node,
  /**
   * Flex fills the parent component's width
   */
  full: PropTypes.bool,
  /**
   * Implements `styled-system` [border](https://styled-system.com/api/#border) utility
   */
  ...propTypes.border,
  /**
   * Implements `styled-system` [color](https://styled-system.com/api/#color)
   */
  ...propTypes.color,
  /**
   * Implements `styled-system` [flexbox](https://styled-system.com/api/#flexbox) utility
   */
  ...propTypes.flexbox,
  /**
   * Implements `styled-system` [layout](https://styled-system.com/api/#layout) utility
   */
  ...propTypes.layout,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
  /**
   * Implements `styled-system` [position](https://styled-system.com/api/#position) utility
   */
  ...propTypes.position,
};

export default Box;
